/**
 * HPQ Navigation v2 — Progress Cart component + header layout injection.
 *
 * Exports: window.hpqNav = { createProgressCart, updateProgressCart }
 *
 * Feature-flagged: only runs when <body class="hpq-nav-v2"> is present.
 * Depends on window.hpqCart being available before cart:updated fires.
 *
 * T3: Desktop header → [HAMBURGER][LOGO][SEARCH-BAR][PROGRESS-CART]
 * T4: Mobile header  → [HAMBURGER][LOGO][ACCOUNT], search bar injected after header
 * T7: Wire progress-cart button to open cart drawer via [data-js-cart-icon]
 */
(function () {
  'use strict';

  var THRESHOLD = (window.hpqFreeShip && window.hpqFreeShip.threshold) || 5900;
  var THRESHOLD_DOLLARS = '$' + Math.floor(THRESHOLD / 100);

  // Free-ship state (threshold + coupon-adjusted effective total + status + "checking…"
  // pending) is owned by hpq-free-ship.js's single ViewModel (window.hpqCartState.getVM()),
  // shared with the cart-page bar + drawer bar. The header progress-cart MUST use the
  // coupon-adjusted total too — keying on the raw cart total makes it claim "FREE SHIPPING"
  // for a cart a coupon has dropped below the threshold, the exact "shows FREE, charged $9 at
  // checkout" bug. Consume vm.freeShip directly — no independent re-derivation — so this bar can
  // never disagree with the totals renderer. Fall back to the legacy window.hpqFreeShip state
  // only if the shared store somehow hasn't initialized yet (load-order safety).
  function getFreeShip() {
    var vm = window.hpqCartState && typeof window.hpqCartState.getVM === 'function' ? window.hpqCartState.getVM() : null;
    if (vm && vm.freeShip) return vm.freeShip;
    var fs = window.hpqFreeShip;
    if (fs && typeof fs.getState === 'function') {
      var legacy = fs.getState();
      return {
        threshold: legacy.threshold,
        remainingCents: legacy.remainingCents,
        pct: legacy.pct,
        pending: legacy.pending,
        status: legacy.status === 'flat' ? 'away' : legacy.status
      };
    }
    return { threshold: THRESHOLD, remainingCents: THRESHOLD, pct: 0, pending: false, status: 'empty' };
  }

  // Branded checkmark SVG (free-shipping unlocked cue). Single source of truth
  // for this file — DOM-injected via innerHTML, which remounts the element on
  // every write, triggering the .hpq-check-icon scale-in animation in CSS.
  var HPQ_CHECK_SVG = '<svg class="hpq-check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#00953B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ──────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────

  function fmtDollars(cents) {
    var d = Math.floor(cents / 100);
    var r = cents % 100;
    return '$' + d + '.' + (r < 10 ? '0' + r : String(r));
  }

  function cartSvg() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');

    // Cart basket
    var basket = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    basket.setAttribute('d', 'M6 6h12l-1.5 7H7.5L6 6z');
    basket.setAttribute('stroke', 'currentColor');
    basket.setAttribute('stroke-width', '1.5');
    basket.setAttribute('stroke-linecap', 'round');
    basket.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(basket);

    // Cart handle
    var handle = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    handle.setAttribute('d', 'M6 6L5 3H2');
    handle.setAttribute('stroke', 'currentColor');
    handle.setAttribute('stroke-width', '1.5');
    handle.setAttribute('stroke-linecap', 'round');
    handle.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(handle);

    // Left wheel
    var wheelL = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    wheelL.setAttribute('cx', '8');
    wheelL.setAttribute('cy', '17');
    wheelL.setAttribute('r', '1.5');
    wheelL.setAttribute('stroke', 'currentColor');
    wheelL.setAttribute('stroke-width', '1.5');
    svg.appendChild(wheelL);

    // Right wheel
    var wheelR = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    wheelR.setAttribute('cx', '16');
    wheelR.setAttribute('cy', '17');
    wheelR.setAttribute('r', '1.5');
    wheelR.setAttribute('stroke', 'currentColor');
    wheelR.setAttribute('stroke-width', '1.5');
    svg.appendChild(wheelR);

    return svg;
  }

  // ──────────────────────────────────────────────
  // createProgressCart
  // Returns a fully-formed <button> element.
  // T7: button carries data-js-cart-icon so hpq-quick-cart.js intercepts it.
  // ──────────────────────────────────────────────

  function createProgressCart() {
    var btn = document.createElement('button');
    btn.className = 'hpq-progress-cart hpq-progress-cart--empty';
    btn.setAttribute('type', 'button');
    btn.setAttribute('aria-label', 'Open cart');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('role', 'button');
    // T7: hpq-quick-cart.js intercepts [data-js-cart-icon] in capture phase.
    btn.setAttribute('data-js-cart-icon', 'bag');

    // Text label
    var text = document.createElement('span');
    text.className = 'hpq-progress-cart__text';
    text.textContent = 'Free shipping on ' + THRESHOLD_DOLLARS + '+';

    // Progress bar track + fill
    var bar = document.createElement('span');
    bar.className = 'hpq-progress-cart__bar';
    var fill = document.createElement('span');
    fill.className = 'hpq-progress-cart__fill';
    fill.style.width = '0%';
    bar.appendChild(fill);

    // Cart icon + count badge
    var iconWrap = document.createElement('span');
    iconWrap.className = 'hpq-progress-cart__icon';
    iconWrap.appendChild(cartSvg());
    var count = document.createElement('span');
    count.className = 'hpq-progress-cart__count';
    count.textContent = '0';
    iconWrap.appendChild(count);

    btn.appendChild(text);
    btn.appendChild(bar);
    btn.appendChild(iconWrap);

    return btn;
  }

  // ──────────────────────────────────────────────
  // updateProgressCart
  // Reads from window.hpqCart and mutates `el` in place.
  // ──────────────────────────────────────────────

  function updateProgressCart(el) {
    if (!el) return;

    var cart = window.hpqCart ? window.hpqCart.getCart() : null;
    var itemCount = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    var freeShip = getFreeShip(); // coupon-adjusted + pending-aware, from the shared ViewModel

    var text = el.querySelector('.hpq-progress-cart__text');
    var fill = el.querySelector('.hpq-progress-cart__fill');
    var count = el.querySelector('.hpq-progress-cart__count');

    if (!text || !fill || !count) return;

    count.textContent = String(itemCount);

    if (freeShip.status === 'pending') {
      // A coupon quote is in flight — neutral note, hold the fill, no stale claim to flash.
      el.classList.remove('hpq-progress-cart--empty');
      text.textContent = 'Checking shipping…';

    } else if (freeShip.status === 'empty') {
      // State (a): empty cart
      el.classList.add('hpq-progress-cart--empty');
      text.textContent = 'Free shipping on ' + THRESHOLD_DOLLARS + '+';
      fill.style.width = '0%';

    } else if (freeShip.status === 'free') {
      // State (c): threshold met (on the coupon-adjusted total)
      el.classList.remove('hpq-progress-cart--empty');
      text.innerHTML = HPQ_CHECK_SVG + ' FREE SHIPPING';
      fill.style.width = '100%';

    } else {
      // State (b): below threshold (status === 'away')
      el.classList.remove('hpq-progress-cart--empty');
      text.textContent = 'Add ' + fmtDollars(freeShip.remainingCents) + ' for free shipping';
      fill.style.width = Math.min(99, freeShip.pct) + '%';
    }
  }

  // ──────────────────────────────────────────────
  // Cycling placeholder prompts — Auntie voice
  // ──────────────────────────────────────────────

  var PLACEHOLDERS = [
    'Ask Auntie anything...',
    'What goes with dumplings tonight?',
    'I want something fiery, not foolish',
    'Cold hands, empty stomach — what should I eat?',
    'Build me a Chongqing night for two',
    'What should I swirl into noodles?',
    'Which hotpot starter tastes like home?',
    'I need a quick weeknight comfort bowl',
    'Give me mala, heat, and a little drama'
  ];

  var MOBILE_PLACEHOLDERS = [
    'Ask Auntie anything...',
    'What goes with dumplings?',
    'Need something spicy?',
    'Hotpot for two?',
    'Best sauce for noodles?',
    'What should I eat tonight?'
  ];

  // ──────────────────────────────────────────────
  // createSearchProxy
  // Returns a wrapper <div> containing a search <input>.
  // Each instance independently cycles through Auntie placeholders.
  // T-S1: Cycling placeholder on idle; clears on focus, restarts on blur.
  // T-S2: Exports input reference; does NOT open Stiletto overlay on focus.
  // Pass `modifier` class to distinguish desktop vs mobile variants.
  // ──────────────────────────────────────────────

  function createSearchProxy(modifierClass) {
    var wrap = document.createElement('div');
    wrap.className = 'hpq-search-proxy ' + modifierClass;

    var activePlaceholders = modifierClass.indexOf('--mobile') !== -1 ? MOBILE_PLACEHOLDERS : PLACEHOLDERS;

    var input = document.createElement('input');
    input.setAttribute('type', 'search');
    input.setAttribute('placeholder', activePlaceholders[0]);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-controls', 'hpq-search-dropdown');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');
    input.className = 'hpq-search-proxy__input';

    // ── Cycling placeholder logic ──
    var cycleIndex = 0;
    var cycleInterval = null;

    function startCycle() {
      if (cycleInterval) return;
      cycleInterval = setInterval(function () {
        cycleIndex = (cycleIndex + 1) % activePlaceholders.length;
        // Fade out → swap → fade in via class toggle
        input.classList.add('hpq-search-proxy__input--ph-fade');
        setTimeout(function () {
          input.setAttribute('placeholder', activePlaceholders[cycleIndex]);
          input.classList.remove('hpq-search-proxy__input--ph-fade');
        }, 200);
      }, 3000);
    }

    function stopCycle() {
      if (cycleInterval) {
        clearInterval(cycleInterval);
        cycleInterval = null;
      }
    }

    // Start cycling immediately
    startCycle();

    // On focus: stop cycling, show functional placeholder
    input.addEventListener('focus', function () {
      stopCycle();
      input.setAttribute('placeholder', 'Search products...');
    });

    // On blur: restart cycling
    input.addEventListener('blur', function () {
      cycleIndex = 0;
      input.setAttribute('placeholder', activePlaceholders[0]);
      startCycle();
    });

    // Auntie avatar icon (replaces magnifier)
    var avatar = document.createElement('span');
    avatar.className = 'hpq-search-proxy__icon hpq-auntie-avatar';
    // Load the animated SVG sprite as an <img> so CSS animations inside the SVG run
    var avatarImg = document.createElement('img');
    avatarImg.src = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') +
      'cdn/shop/files/hpq-auntie-fu-face.svg';
    // Fallback: try asset_url pattern
    var assetEl = document.getElementById('hpq-auntie-avatar-src');
    if (assetEl) avatarImg.src = assetEl.textContent.trim();
    avatarImg.alt = 'Auntie';
    avatarImg.width = 44;
    avatarImg.height = 44;
    avatarImg.className = 'hpq-auntie-avatar__img';
    avatarImg.setAttribute('loading', 'eager');
    avatar.appendChild(avatarImg);
    wrap.appendChild(avatar);

    wrap.appendChild(input);
    return { wrap: wrap, input: input };
  }

  // ──────────────────────────────────────────────
  // injectDesktopLayout  (T3)
  // [HAMBURGER][LOGO][SEARCH-BAR][PROGRESS-CART]
  // ──────────────────────────────────────────────

  function injectDesktopLayout() {
    // Target the lower row specifically — the upper row is a secondary strip that
    // becomes empty/redundant in hpq-nav-v2 mode and should be hidden.
    var lowerRow = document.querySelector('.header__row-desktop.lower');
    var leftSeg = lowerRow && lowerRow.querySelector('.header__row-segment-desktop.left');
    var rightSeg = lowerRow && lowerRow.querySelector('.header__row-segment-desktop.right');

    if (!leftSeg || !rightSeg) {
      console.warn('[hpq-nav] Desktop header lower row segments not found.');
      return null;
    }

    // Clone the mobile hamburger and prepend to desktop left segment.
    // The clone delegates clicks to the original so Stiletto's event handlers fire.
    var mobileHamburger = document.querySelector('.header__row-mobile .header__icon-menu');
    if (mobileHamburger) {
      var desktopHamburger = mobileHamburger.cloneNode(true);
      desktopHamburger.setAttribute('aria-label', 'Open menu');
      desktopHamburger.addEventListener('click', function () {
        mobileHamburger.click();
      });
      leftSeg.insertBefore(desktopHamburger, leftSeg.firstChild);
    } else {
      console.warn('[hpq-nav] Mobile hamburger (.header__icon-menu) not found to clone.');
    }

    // Inject search proxy into desktop right segment
    var desktopProxy = createSearchProxy('hpq-search-proxy--desktop');
    rightSeg.insertBefore(desktopProxy.wrap, rightSeg.firstChild);

    // Inject progress-cart into desktop right segment (after search proxy)
    var progressCart = createProgressCart();
    rightSeg.appendChild(progressCart);

    return { progressCart: progressCart, desktopInput: desktopProxy.input };
  }

  // ──────────────────────────────────────────────
  // injectMobileLayout  (T4)
  // Mobile header: [HAMBURGER][LOGO][ACCOUNT]
  // Search bar injected as sibling AFTER header section wrapper.
  // Account icon injected into right segment if Stiletto didn't render one.
  // ──────────────────────────────────────────────

  function injectMobileLayout() {
    var headerSection = document.querySelector('.shopify-section.header__outer-wrapper');
    if (!headerSection) {
      console.warn('[hpq-nav] Header section wrapper (.shopify-section.header__outer-wrapper) not found.');
      return;
    }

    // Inject account icon if not already present (customer accounts may be disabled in theme settings)
    var existingAccount = document.querySelector('.header__row-mobile .header__icon-touch--account');
    if (!existingAccount) {
      var mobileRow = document.querySelector('.header__row-mobile');
      if (mobileRow) {
        var accountHrefSource = document.querySelector('.header__icon-touch--account[href], a[href*="/account/login"], a[href$="/account"]');
        var acctLink = document.createElement('a');
        acctLink.href = accountHrefSource ? accountHrefSource.getAttribute('href') : '/account';
        acctLink.className = 'hpq-account-icon';
        acctLink.setAttribute('aria-label', 'Account');
        acctLink.setAttribute('data-hpq-account-link', 'mobile');
        // Person SVG icon (matches Stiletto's icon style)
        acctLink.innerHTML = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="12" cy="7.5" r="3.5" stroke="currentColor" stroke-width="1.2"/><path d="M4.5 20.5c0-4.142 3.358-7.5 7.5-7.5s7.5 3.358 7.5 7.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
        mobileRow.appendChild(acctLink);
      }
    }

    // Build mobile search bar
    var mobileSearchBar = document.createElement('div');
    mobileSearchBar.className = 'hpq-mobile-search-bar';

    var mobileProxy = createSearchProxy('hpq-search-proxy--mobile');
    mobileSearchBar.appendChild(mobileProxy.wrap);

    headerSection.insertAdjacentElement('afterend', mobileSearchBar);

    return mobileProxy.input;
  }

  // ──────────────────────────────────────────────
  // injectMobileBottomBar  (T5)
  // Fixed bottom bar with progress-cart for mobile.
  // Visibility managed via events + MutationObserver.
  // ──────────────────────────────────────────────

  function injectMobileBottomBar() {
    var bar = document.createElement('div');
    bar.className = 'hpq-mobile-bottom-bar';

    var progressCart = createProgressCart();
    bar.appendChild(progressCart);
    document.body.appendChild(bar);

    // Initial state
    updateProgressCart(progressCart);

    // Helper: re-evaluate visibility from body attributes
    function syncVisibility() {
      var hidden =
        document.body.getAttribute('mobile-menu-open') === 'true' ||
        document.body.getAttribute('quick-search-open') === 'true' ||
        document.body.getAttribute('data-fluorescent-overlay-open') === 'true';
      bar.classList.toggle('hpq-mobile-bottom-bar--hidden', hidden);
    }

    // Cart drawer open/close events
    document.addEventListener('quick-cart:open', function () {
      bar.classList.add('hpq-mobile-bottom-bar--hidden');
    });
    document.addEventListener('quick-cart:close', function () {
      syncVisibility();
    });

    // Body attribute changes (mobile menu, quick search, overlay)
    var observer = new MutationObserver(syncVisibility);
    observer.observe(document.body, { attributes: true, attributeFilter: ['mobile-menu-open', 'quick-search-open', 'data-fluorescent-overlay-open'] });

    return progressCart;
  }

  // ──────────────────────────────────────────────
  // initScrollTransition  (Feature 4)
  // Adds/removes .hpq-scrolled on the header outer wrapper.
  // Threshold: the scroll position where the first section's bottom edge
  // leaves the header area (heroHeight - headerHeight). Falls back to
  // window.innerHeight / 2 if no first section is found.
  // ──────────────────────────────────────────────

  function initScrollTransition() {
    var wrapper = document.querySelector('.shopify-section.header__outer-wrapper') ||
                  document.querySelector('.header__outer-wrapper');
    if (!wrapper) return;

    function getThreshold() {
      var headerHeight = wrapper.offsetHeight || 80;
      var firstSection = document.querySelector('#main .shopify-section:first-child');
      if (firstSection) {
        // Switch when the bottom of the first section scrolls past the header bottom
        return Math.max(80, firstSection.offsetHeight - headerHeight);
      }
      return Math.max(80, window.innerHeight / 2);
    }

    function onScroll() {
      var scrolled = window.scrollY > getThreshold();
      wrapper.classList.toggle('hpq-scrolled', scrolled);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // Set initial state (e.g. page loaded mid-scroll / browser restores position)
    onScroll();
  }

  // ──────────────────────────────────────────────
  // injectDrawerAccountLinks  (T-M2)
  // Appends extra account links (Orders, Addresses) below the existing
  // Stiletto account item once the drawer first opens.
  // Only runs once — guard prevents duplicate injection.
  // ──────────────────────────────────────────────

  function injectDrawerAccountLinks() {
    var secondary = document.querySelector('.drawer-menu__secondary-links');
    if (!secondary) return;

    // Guard: skip if already injected
    if (secondary.querySelector('.hpq-drawer-account')) return;

    // Find the existing Stiletto account item to insert after it
    var stilettoItem = secondary.querySelector('.drawer-menu__item--account-item');

    var links = [
      { label: 'My Orders', href: '/account/orders' },
      { label: 'Addresses', href: '/account/addresses' }
    ];

    var fragment = document.createDocumentFragment();
    for (var i = 0; i < links.length; i++) {
      var li = document.createElement('li');
      li.className = 'drawer-menu__item hpq-drawer-account';

      var a = document.createElement('a');
      a.className = 'drawer-menu__link';
      a.href = links[i].href;
      a.textContent = links[i].label;

      li.appendChild(a);
      fragment.appendChild(li);
    }

    if (stilettoItem && stilettoItem.nextSibling) {
      secondary.insertBefore(fragment, stilettoItem.nextSibling);
    } else if (stilettoItem) {
      secondary.appendChild(fragment);
    } else {
      secondary.appendChild(fragment);
    }
  }

  // ──────────────────────────────────────────────
  // Init
  // ──────────────────────────────────────────────

  function init() {
    // Feature flag
    if (!document.body.classList.contains('hpq-nav-v2')) return;

    // Health check
    if (!window.hpqCart) {
      console.warn('[hpq-nav] window.hpqCart not found. Progress cart will not reflect cart state until hpq-local-cart.js loads.');
    }

    // Feature 4: Scroll-triggered transparent→white header transition
    initScrollTransition();

    // T3: Inject desktop layout — returns { progressCart, desktopInput }
    var desktopResult = injectDesktopLayout();

    // T4: Inject mobile search bar after header — returns mobileInput
    var mobileInput = injectMobileLayout();

    // T-S2: Export search inputs so hpq-search-dropdown.js can attach to them
    window.hpqNav.searchInputs = {
      desktop: desktopResult ? desktopResult.desktopInput : null,
      mobile: mobileInput || null
    };
    document.dispatchEvent(new CustomEvent('hpq:search-proxy-ready'));

    // T5: Inject mobile sticky bottom bar
    injectMobileBottomBar();

    // T7: Initial update of progress-cart state once hpqCart is available
    var progressCartEl = desktopResult ? desktopResult.progressCart : null;
    if (progressCartEl) {
      updateProgressCart(progressCartEl);
    }

    // T7: Refresh all mounted progress-cart elements. hpq:cart-changed fires on every cart
    // change, coupon quote landed/cleared, and quote-in-flight (→ "Checking…"), so the header
    // indicator stays in lock-step with the cart bar, drawer bar, coupon summary, and checkout.
    function refreshAllProgressCarts() {
      var elements = document.querySelectorAll('.hpq-progress-cart');
      for (var i = 0; i < elements.length; i++) {
        updateProgressCart(elements[i]);
      }
    }
    document.addEventListener('cart:updated', refreshAllProgressCarts);
    document.addEventListener('hpq:cart-changed', refreshAllProgressCarts);

    // T-M2: Inject account links when drawer first opens.
    // Watch body[mobile-menu-open] attribute (set by Stiletto when drawer activates).
    var drawerAccountObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'mobile-menu-open' &&
            document.body.getAttribute('mobile-menu-open') === 'true') {
          injectDrawerAccountLinks();
          break;
        }
      }
    });
    drawerAccountObserver.observe(document.body, { attributes: true, attributeFilter: ['mobile-menu-open'] });
  }

  // ──────────────────────────────────────────────
  // Public API (must be set before init() runs so
  // init() can write searchInputs into the object)
  // ──────────────────────────────────────────────

  window.hpqNav = {
    createProgressCart: createProgressCart,
    updateProgressCart: updateProgressCart,
    searchInputs: null
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
